import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from "@hookform/resolvers/zod";
import { type FieldPath, useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import toast from 'react-hot-toast';
import { useState, useEffect } from 'react';
import { Plus, Trash2 } from "lucide-react";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FancyMultiSelect, Options } from "@/components/ui/fancy-multi-select";
import { FancyMultiSelectPopover } from "@/components/ui/fancy-multi-select-popover";
import { apiErrorMessage, apiValidationFieldErrors, backendClient } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { getJurisdiction } from '@/jurisdictions';
import { getSubregionConfig, isUsRegion, regionRequiresForum } from '@/jurisdictions/regions';
import type { Matter } from '@/types';
import { Save } from "lucide-react";
import useMatter from "@/hooks/useMatter";
import { useMatterLabels } from "@/lib/useMatterLabels";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loading } from '../shared';

// Client schema
const clientSchema = z.object({
  name: z.string().max(100, { message: "Client name must be at most 100 characters." }),
  role: z.string().max(50, { message: "Client role must be at most 50 characters." })
});

const updateMatterSchema = z.object({
  name: z.string().min(1, { message: "Case name is required." }).max(75, { message: "Case name must be at most 75 characters." }),
  matter_no: z.string().trim().max(25, { message: "Matter number must be at most 25 characters." }).optional(),
  case_reference: z.string().optional(),
  venue: z.string().optional(),

  // Core fields
  region: z.string().optional(),
  subregion: z.string().optional(),
  forum: z.string().optional(),

  // Case Overview fields
  clients: z.array(clientSchema).max(50, { message: "At most 50 clients allowed." }).optional(),
  governing_law: z.string().optional(),
  arbitration_rules: z.string().max(30, { message: "Arbitration rules must be at most 30 characters." }).optional(),
  seat_of_arbitration: z.string().max(30, { message: "Seat of arbitration must be at most 30 characters." }).optional(),
  court: z.string().optional(),

  // Underlying Dispute fields
  main_causes_of_action: z.array(z.string().max(100, { message: "Each cause of action must be at most 100 characters." })).max(10, { message: "At most 10 causes of action allowed." }).optional(),
  claimant_position: z.string().max(20000, { message: "Claimant position must be at most 20000 characters." }).optional(),
  defendant_position: z.string().max(20000, { message: "Defendant position must be at most 20000 characters." }).optional(),

  // Instructions fields
  have_instructed_counsel: z.boolean().optional(),
  counsel_name: z.array(z.string().max(100, { message: "Each counsel name must be at most 100 characters." })).max(5, { message: "At most 5 counsel names allowed." }).optional(),
  chambers: z.string().max(100, { message: "Chambers must be at most 100 characters." }).optional(),
  opposing_law_firms: z.array(z.string().max(100, { message: "Each law firm must be at most 100 characters." })).max(10, { message: "At most 10 law firms allowed." }).optional(),
  has_opposing_instructed_counsel: z.boolean().optional(),
  opposing_counsel_name: z.array(z.string().max(100, { message: "Each opposing counsel name must be at most 100 characters." })).max(5, { message: "At most 5 opposing counsel names allowed." }).optional(),
  opposing_counsel_chambers: z.string().max(100, { message: "Opposing counsel chambers must be at most 100 characters." }).optional(),
  principal_client_contact: z.string().max(100, { message: "Principal client contact must be at most 100 characters." }).optional(),

  // Custom instructions field
  custom_instructions: z.string().max(5000, { message: "Custom instructions must be at most 5000 characters." }).optional(),
});

type FormData = z.infer<typeof updateMatterSchema>;
type MatterSettingsFieldName = FieldPath<FormData>;

interface MatterSettingsProps {
  matter: Matter;
  className?: string;
}

const MatterSettings = ({ matter, className }: MatterSettingsProps) => (
  <MatterSettingsForm key={matter.guid} matter={matter} className={className} />
);

const MatterSettingsForm = ({ matter, className }: MatterSettingsProps) => {
  const { t } = useTranslation('common');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateMatter } = useMatter(matter.guid);

  const [activeTab, setActiveTab] = useState('basic');

  const form = useForm<FormData>({
    resolver: zodResolver(updateMatterSchema),
    defaultValues: {
      name: matter.name || '',
      matter_no: matter.matter_no || '',
      case_reference: matter.case_reference || '',
      venue: matter.venue || '',
      region: matter.region || 'UK',  // Default to UK if somehow missing
      subregion: matter.subregion || '',
      forum: matter.forum || '',
      // Case Overview
      clients: matter.clients || [],
      governing_law: matter.governing_law || '',
      arbitration_rules: matter.arbitration_rules || '',
      seat_of_arbitration: matter.seat_of_arbitration || '',
      court: matter.court || '',
      // Underlying Dispute
      main_causes_of_action: matter.main_causes_of_action || [],
      claimant_position: matter.claimant_position || '',
      defendant_position: matter.defendant_position || '',
      // Instructions
      have_instructed_counsel: matter.have_instructed_counsel || false,
      counsel_name: matter.counsel_name || [],
      chambers: matter.chambers || '',
      opposing_law_firms: matter.opposing_law_firms || [],
      has_opposing_instructed_counsel: matter.has_opposing_instructed_counsel || false,
      opposing_counsel_name: matter.opposing_counsel_name || [],
      opposing_counsel_chambers: matter.opposing_counsel_chambers || '',
      principal_client_contact: matter.principal_client_contact || '',
      custom_instructions: matter.custom_instructions || '',
    },
  });

  const watchRegion = form.watch("region");
  const watchForum = form.watch("forum");
  const watchHaveCounsel = form.watch("have_instructed_counsel");
  const watchHasOpposingCounsel = form.watch("has_opposing_instructed_counsel");
  const jurisdiction = getJurisdiction(watchRegion ?? "UK", watchForum ?? null);
  const opts = jurisdiction.form_options;
  const subregionConfig = getSubregionConfig(watchRegion);
  const usProfile = isUsRegion(watchRegion);
  // Copy follows the live form selection (watchRegion), not the saved matter region.
  const label = useMatterLabels(watchRegion);
  // Respondent wording only for UK-profile non-litigation (incl. unset forum); US always uses Defendant.
  const isRespondentParty = !usProfile && watchForum !== "Litigation";

  // Reset forum when region changes to a profile that has no forum
  useEffect(() => {
    if (!regionRequiresForum(watchRegion)) {
      form.setValue("forum", undefined);
    }
  }, [watchRegion, form]);

  const addClient = () => {
    const currentClients = form.getValues("clients") || [];
    if (currentClients.length < 50) {
      form.setValue("clients", [...currentClients, { name: "", role: "" }]);
    }
  };

  const removeClient = (index: number) => {
    const currentClients = form.getValues("clients") || [];
    form.setValue("clients", currentClients.filter((_, i) => i !== index));
  };

  const getMainCausesOptions = (): Options[] => {
    return opts.causes_of_action.map(c => ({ value: c, label: c }));
  };

  async function onSubmit(values: FormData) {
    try {
      setIsSubmitting(true);

      const trimmedMatterNo = values.matter_no?.trim() ?? "";

      // Clean up form data before submission
      const submitData = {
        ...values,
        matter_no: trimmedMatterNo || null,
        subregion: values.subregion?.trim() || null,
        forum: regionRequiresForum(values.region) ? values.forum : null,
        main_causes_of_action: values.main_causes_of_action || [],
        opposing_law_firms: values.opposing_law_firms || [],
        // Clear counsel fields if not instructed
        counsel_name: values.have_instructed_counsel ? values.counsel_name : [],
        chambers: values.have_instructed_counsel ? values.chambers : "",
        // Clear opposing counsel fields if not instructed
        opposing_counsel_name: values.has_opposing_instructed_counsel ? values.opposing_counsel_name : [],
        opposing_counsel_chambers: values.has_opposing_instructed_counsel ? values.opposing_counsel_chambers : "",
      };

      const response = await backendClient.patch(`/matter/update/${matter.guid}`, submitData);
      if (response.status === 200) {
        toast.success(t('successfully-updated'));
        // Refresh the matter data after successful update
        mutateMatter();
        // Update the form values with the new data
        form.reset(values);
      }
    } catch (error) {
      console.error("Error updating matter:", error);
      let firstInvalidField: MatterSettingsFieldName | null = null;
      for (const fieldError of apiValidationFieldErrors(error)) {
        const field = fieldError.field as MatterSettingsFieldName;
        form.setError(field, { message: fieldError.message });
        firstInvalidField ??= field;
      }
      if (firstInvalidField) {
        form.setFocus(firstInvalidField);
      } else {
        toast.error(apiErrorMessage(error) || "An error occurred while updating the matter.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className={`h-full ${className || ''}`}>
      <div className="bg-white rounded-lg shadow-xs border border-gray-200 h-full flex flex-col">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="h-full flex flex-col">
            <div className="pt-6 px-6 flex-1 flex flex-col min-h-0 overflow-hidden">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex-1 flex flex-col min-h-0 overflow-hidden">
                <TabsList className="grid w-full grid-cols-4 shrink-0">
                  <TabsTrigger value="basic">Basic Info</TabsTrigger>
                  <TabsTrigger value="overview">Case Overview</TabsTrigger>
                  <TabsTrigger value="dispute">Underlying Dispute</TabsTrigger>
                  <TabsTrigger value="legal">Instructions</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4 mt-4 overflow-y-auto flex-1 min-h-0 pl-1 pr-4">
                  <FormField
                    control={form.control}
                    name="matter_no"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="md:block md:pb-0.5">Matter number <span className="text-sm font-normal text-muted-foreground">(optional)</span></FormLabel>
                        <FormControl>
                          <Input maxLength={25} placeholder="Enter the matter number" {...field} value={field.value ?? ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="md:block md:pb-0.5">Case name</FormLabel>
                        <FormControl>
                          <Input maxLength={75} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="case_reference"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="md:block md:pb-0.5">Case reference</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="region"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="md:block md:pb-0.5">Region</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value} disabled>
                          <FormControl>
                            <SelectTrigger disabled>
                              <SelectValue placeholder="Select a region" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value={field.value || 'UK'}>{field.value || 'UK'}</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {subregionConfig.options.length > 0 && (
                    <FormField
                      control={form.control}
                      name="subregion"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="md:block md:pb-0.5">Venue</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a Venue" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {subregionConfig.options.map((opt) => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {regionRequiresForum(watchRegion) && (
                    <FormField
                      control={form.control}
                      name="forum"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="md:block md:pb-0.5">Type</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value} disabled>
                            <FormControl>
                              <SelectTrigger disabled>
                                <SelectValue placeholder="Select a type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Arbitration">Arbitration</SelectItem>
                              <SelectItem value="Litigation">Litigation</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="governing_law"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="md:block md:pb-0.5">Governing law</FormLabel>
                        <FormControl>
                          <FancyMultiSelect
                            value={field.value ? [{ value: field.value, label: field.value }] : []}
                            onValueChange={(values) => {
                              field.onChange(values.length > 0 ? values[0].value : "");
                            }}
                            placeholder="Select or enter governing law"
                            options={opts.governing_law.map(opt => ({ value: opt, label: opt }))}
                            freeText={true}
                            singleSelect={true}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {opts.venues.length > 0 && (
                    <FormField
                      control={form.control}
                      name="venue"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="md:block md:pb-0.5">Venue</FormLabel>
                          <FormControl>
                            <FancyMultiSelect
                              value={field.value ? [{ value: field.value, label: field.value }] : []}
                              onValueChange={(values) => {
                                field.onChange(values.length > 0 ? values[0].value : "");
                              }}
                              placeholder="Select or enter venue"
                              options={opts.venues.map(opt => ({ value: opt, label: opt }))}
                              freeText={true}
                              singleSelect={true}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </TabsContent>

                <TabsContent value="overview" className="space-y-4 mt-4 overflow-y-auto flex-1 min-h-0 pl-1 pr-4">
                  <div className="space-y-4 relative">
                    <div className="flex items-center justify-between">
                      <FormLabel>Clients</FormLabel>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addClient}
                        disabled={(form.watch("clients") || []).length >= 50}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Client
                      </Button>
                    </div>

                    {form.watch("clients")?.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No clients added yet. Click "Add Client" to add a client.</p>
                    ) : (
                      <div className="space-y-2">
                        <div className="grid grid-cols-12 gap-2 pb-2 border-b pl-1">
                          <div className="col-span-5 text-sm font-medium text-gray-700">Name</div>
                          <div className="col-span-6 text-sm font-medium text-gray-700">Role</div>
                          <div className="col-span-1"></div>
                        </div>
                        <div className="max-h-48 overflow-y-auto space-y-2">
                          {form.watch("clients")?.map((_, index) => (
                            <div key={index} className="grid grid-cols-12 gap-2 py-2 border-b pl-1">
                              <div className="col-span-5">
                                <FormField
                                  control={form.control}
                                  name={`clients.${index}.name`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormControl>
                                        <Input
                                          placeholder="Enter name"
                                          maxLength={100}
                                          {...field}
                                        />
                                      </FormControl>
                                    </FormItem>
                                  )}
                                />
                              </div>
                              <div className="col-span-6">
                                <FormField
                                  control={form.control}
                                  name={`clients.${index}.role`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormControl>
                                        <FancyMultiSelectPopover
                                          value={field.value ? [{ value: field.value, label: field.value }] : []}
                                          onValueChange={(values) => {
                                            field.onChange(values.length > 0 ? values[0].value : "");
                                          }}
                                          placeholder="Select or enter role"
                                          options={opts.client_roles.map(opt => ({ value: opt, label: opt }))}
                                          freeText={true}
                                          singleSelect={true}
                                          maxLength={50}
                                        />
                                      </FormControl>
                                    </FormItem>
                                  )}
                                />
                              </div>
                              <div className="col-span-1 flex items-center">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeClient(index)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>



                  {opts.arbitration_rules && opts.arbitration_rules.length > 0 && (
                    <>
                      <FormField
                        control={form.control}
                        name="arbitration_rules"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="md:block md:pb-0.5">Arbitration rules</FormLabel>
                            <FormControl>
                              <FancyMultiSelect
                                value={field.value ? [{ value: field.value, label: field.value }] : []}
                                onValueChange={(values) => {
                                  field.onChange(values.length > 0 ? values[0].value : "");
                                }}
                                placeholder="Select or enter arbitration rules"
                                options={(opts.arbitration_rules ?? []).map(opt => ({ value: opt, label: opt }))}
                                freeText={true}
                                singleSelect={true}
                                maxLength={30}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="seat_of_arbitration"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="md:block md:pb-0.5">Seat of arbitration</FormLabel>
                            <FormControl>
                              <FancyMultiSelect
                                value={field.value ? [{ value: field.value, label: field.value }] : []}
                                onValueChange={(values) => {
                                  field.onChange(values.length > 0 ? values[0].value : "");
                                }}
                                placeholder="Select or enter seat of arbitration"
                                options={(opts.seats_of_arbitration ?? []).map(opt => ({ value: opt, label: opt }))}
                                freeText={true}
                                singleSelect={true}
                                maxLength={30}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}

                  {usProfile && (
                    <FormField
                      control={form.control}
                      name="court"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="md:block md:pb-0.5">Court</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter court name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="principal_client_contact"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="md:block md:pb-0.5">Principal client contact</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter principal client contact" maxLength={100} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {!usProfile && (
                    <>
                      <FormField
                        control={form.control}
                        name="have_instructed_counsel"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="md:block md:pb-0.5">
                              Have you instructed counsel?
                            </FormLabel>
                            <FormControl>
                              <div className="inline-flex rounded-lg border border-input bg-white p-0.5">
                                <button
                                  type="button"
                                  onClick={() => field.onChange(false)}
                                  className={cn(
                                    "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
                                    !field.value
                                      ? "bg-primary text-primary-foreground"
                                      : "text-muted-foreground hover:text-foreground"
                                  )}
                                >
                                  No
                                </button>
                                <button
                                  type="button"
                                  onClick={() => field.onChange(true)}
                                  className={cn(
                                    "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
                                    field.value
                                      ? "bg-primary text-primary-foreground"
                                      : "text-muted-foreground hover:text-foreground"
                                  )}
                                >
                                  Yes
                                </button>
                              </div>
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      {watchHaveCounsel && (
                        <div className="ml-6 space-y-4">
                          <FormField
                            control={form.control}
                            name="counsel_name"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="md:block md:pb-0.5">Counsel name(s)</FormLabel>
                                <FormControl>
                                  <FancyMultiSelect
                                    value={field.value?.map(v => ({ value: v, label: v })) || []}
                                    onValueChange={(values) => {
                                      field.onChange(values.map(v => v.value));
                                    }}
                                    placeholder="Enter counsel names"
                                    options={[]}
                                    freeText={true}
                                    maxLength={100}
                                    maxItems={5}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="chambers"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="md:block md:pb-0.5">Chambers</FormLabel>
                                <FormControl>
                                  <Input placeholder="Enter chambers" maxLength={100} {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      )}
                    </>
                  )}

                  <FormField
                    control={form.control}
                    name="opposing_law_firms"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="md:block md:pb-0.5">Opposing law firm(s)</FormLabel>
                        <FormControl>
                          <FancyMultiSelect
                            value={field.value?.map(v => ({ value: v, label: v })) || []}
                            onValueChange={(values) => {
                              field.onChange(values.map(v => v.value));
                            }}
                            placeholder="Enter opposing law firms"
                            options={[]}
                            freeText={true}
                            maxLength={100}
                            maxItems={10}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {!usProfile && (
                    <>
                      <FormField
                        control={form.control}
                        name="has_opposing_instructed_counsel"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="md:block md:pb-0.5">
                              Has the opposing law firm instructed counsel?
                            </FormLabel>
                            <FormControl>
                              <div className="inline-flex rounded-lg border border-input bg-white p-0.5">
                                <button
                                  type="button"
                                  onClick={() => field.onChange(false)}
                                  className={cn(
                                    "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
                                    !field.value
                                      ? "bg-primary text-primary-foreground"
                                      : "text-muted-foreground hover:text-foreground"
                                  )}
                                >
                                  No
                                </button>
                                <button
                                  type="button"
                                  onClick={() => field.onChange(true)}
                                  className={cn(
                                    "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
                                    field.value
                                      ? "bg-primary text-primary-foreground"
                                      : "text-muted-foreground hover:text-foreground"
                                  )}
                                >
                                  Yes
                                </button>
                              </div>
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      {watchHasOpposingCounsel && (
                        <div className="ml-6 space-y-4">
                          <FormField
                            control={form.control}
                            name="opposing_counsel_name"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="md:block md:pb-0.5">Opposing counsel name(s)</FormLabel>
                                <FormControl>
                                  <FancyMultiSelect
                                    value={field.value?.map(v => ({ value: v, label: v })) || []}
                                    onValueChange={(values) => {
                                      field.onChange(values.map(v => v.value));
                                    }}
                                    placeholder="Enter opposing counsel names"
                                    options={[]}
                                    freeText={true}
                                    maxLength={100}
                                    maxItems={5}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="opposing_counsel_chambers"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="md:block md:pb-0.5">Opposing counsel chambers</FormLabel>
                                <FormControl>
                                  <Input placeholder="Enter opposing counsel chambers" maxLength={100} {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      )}
                    </>
                  )}
                </TabsContent>

                <TabsContent value="dispute" className="space-y-4 mt-4 overflow-y-auto flex-1 min-h-0 pl-1 pr-4">
                  {watchRegion && (
                    <FormField
                      control={form.control}
                      name="main_causes_of_action"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="md:block md:pb-0.5">Main causes of action</FormLabel>
                          <FormControl>
                            <FancyMultiSelect
                              value={field.value?.map(v => ({ value: v, label: v })) || []}
                              onValueChange={(values) => {
                                field.onChange(values.map(v => v.value));
                              }}
                              placeholder="Select causes of action"
                              options={getMainCausesOptions()}
                              freeText={true}
                              maxLength={100}
                              maxItems={10}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="claimant_position"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="md:block md:pb-0.5">{label('claimant-position-label')}</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Textarea
                              placeholder={label('claimant-position-placeholder')}
                              className="min-h-24 pr-16"
                              maxLength={20000}
                              {...field}
                            />
                            <div className="absolute bottom-2 right-2 text-xs text-muted-foreground">
                              {field.value?.length || 0}/20000
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="defendant_position"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="md:block md:pb-0.5">
                          {isRespondentParty
                            ? label('respondent-position-label')
                            : label('defendant-position-label')}
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Textarea
                              placeholder={isRespondentParty
                                ? label('respondent-position-placeholder')
                                : label('defendant-position-placeholder')}
                              className="min-h-24 pr-16"
                              maxLength={20000}
                              {...field}
                            />
                            <div className="absolute bottom-2 right-2 text-xs text-muted-foreground">
                              {field.value?.length || 0}/20000
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TabsContent>

                <TabsContent value="legal" className="space-y-4 mt-4 overflow-y-auto flex-1 min-h-0 pl-1 pr-4">
                  <FormField
                    control={form.control}
                    name="custom_instructions"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="md:block md:pb-0.5">Custom instructions</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Textarea
                              placeholder="Enter any instructions or context you'd like Crimson to consider on this matter"
                              rows={12}
                              maxLength={5000}
                              className="pr-16"
                              {...field}
                            />
                            <div className="absolute bottom-2 right-2 text-xs text-muted-foreground">
                              {field.value?.length || 0}/5000
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TabsContent>

              </Tabs>
            </div>
            <div className="p-4 shrink-0">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors text-primary-foreground bg-primary hover:bg-primary/90 h-auto px-4 py-2 disabled:pointer-events-none disabled:opacity-50"
              >
                {isSubmitting ? (
                  <Loading text={t('saving')} inline size='sm' />
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {t('save-changes')}
                  </>
                )}
              </button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default MatterSettings;
